import React, { useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { useCalendarApp, ScheduleXCalendar } from '@schedule-x/react';
import { createViewDay } from '@schedule-x/calendar';
import { createEventModalPlugin } from '@schedule-x/event-modal';
import '@schedule-x/theme-default/dist/index.css';
import { MeetingItem } from '../../lib/api';
import { Calendar, Clock, ChevronRight } from 'lucide-react';

interface ScheduleXMeetingCalendarProps {
  meetings: MeetingItem[];
  onSlotClick?: (dateTimeStr: string) => void;
  minDate?: string; // 'YYYY-MM-DD'
  maxDate?: string; // 'YYYY-MM-DD'
  startHour?: string; // '10:00'
  endHour?: string;   // '18:00'
  readOnly?: boolean;
}

const EXPO_DAY_1 = '2027-07-27';
const EXPO_DAY_2 = '2027-07-28';

function toTemporalZonedDateTime(dtStr: string): any {
  try {
    if (dtStr.includes('[') && dtStr.includes(']')) {
      return Temporal.ZonedDateTime.from(dtStr);
    }
    const clean = dtStr.trim().replace(' ', 'T');
    const withSecs = clean.length === 16 ? `${clean}:00` : clean;
    if (!withSecs.includes('+') && !withSecs.includes('Z')) {
      return Temporal.ZonedDateTime.from(`${withSecs}+05:30[Asia/Kolkata]`);
    }
    return Temporal.ZonedDateTime.from(withSecs);
  } catch {
    return Temporal.ZonedDateTime.from('2027-07-27T10:00:00+05:30[Asia/Kolkata]');
  }
}

interface SingleDayCalendarProps {
  selectedDay: string;
  meetings: MeetingItem[];
  onSlotClick?: (dateTimeStr: string) => void;
  readOnly: boolean;
  minDate: string;
  maxDate: string;
  startHour: string;
  endHour: string;
  onDaySwitched?: (day: string) => void;
}

function SingleDayCalendar({
  selectedDay,
  meetings,
  onSlotClick,
  readOnly,
  minDate,
  maxDate,
  startHour,
  endHour,
  onDaySwitched,
}: SingleDayCalendarProps) {
  const eventModal = useMemo(() => createEventModalPlugin(), []);

  // Format meetings to Schedule-X event specifications with Temporal types
  const formattedEvents = useMemo(() => {
    return meetings.map((m) => ({
      id: m.id,
      title: m.title,
      start: toTemporalZonedDateTime(m.start),
      end: toTemporalZonedDateTime(m.end),
      location: m.location,
      description: m.agenda || `Meeting with ${m.targetName}`,
      people: [m.requesterName, m.targetName].filter(Boolean) as string[],
    }));
  }, [meetings]);

  const dayView = useMemo(() => createViewDay(), []);

  const calendarConfig = useMemo(() => {
    return {
      views: [dayView] as [typeof dayView, ...typeof dayView[]],
      defaultView: dayView.name,
      selectedDate: Temporal.PlainDate.from(selectedDay),
      minDate: Temporal.PlainDate.from(minDate),
      maxDate: Temporal.PlainDate.from(maxDate),
      dayBoundaries: {
        start: startHour,
        end: endHour,
      },
      events: formattedEvents,
      plugins: [eventModal],
      callbacks: {
        onClickDateTime(dateTime: any) {
          if (!readOnly && onSlotClick) {
            const str = typeof dateTime === 'string' ? dateTime : dateTime?.toString?.() || '';
            const normalized = str.includes('T') ? str.replace('T', ' ').slice(0, 16) : str.slice(0, 16);
            onSlotClick(normalized);
          }
        },
        onSelectedDateUpdate(dateObj: any) {
          if (onDaySwitched && dateObj) {
            const dateStr = dateObj.toString().slice(0, 10);
            if (dateStr === EXPO_DAY_1 || dateStr === EXPO_DAY_2) {
              onDaySwitched(dateStr);
            }
          }
        },
      },
    };
  }, [dayView, selectedDay, minDate, maxDate, startHour, endHour, formattedEvents, eventModal, readOnly, onSlotClick, onDaySwitched]);

  const calendar = useCalendarApp(calendarConfig);

  return (
    <div className="w-full h-full min-h-[550px] sx-calendar-wrapper bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
      {calendar ? (
        <ScheduleXCalendar calendarApp={calendar} />
      ) : (
        <div className="flex items-center justify-center h-64 text-xs text-slate-400">
          Loading day schedule...
        </div>
      )}
    </div>
  );
}

export function ScheduleXMeetingCalendar({
  meetings,
  onSlotClick,
  minDate = EXPO_DAY_1,
  maxDate = EXPO_DAY_2,
  startHour = '10:00',
  endHour = '18:00',
  readOnly = false,
}: ScheduleXMeetingCalendarProps) {
  const [activeDay, setActiveDay] = useState<string>(EXPO_DAY_1);

  const day1Meetings = useMemo(
    () => meetings.filter((m) => m.start.startsWith(EXPO_DAY_1)),
    [meetings]
  );
  const day2Meetings = useMemo(
    () => meetings.filter((m) => m.start.startsWith(EXPO_DAY_2)),
    [meetings]
  );

  return (
    <div className="space-y-4">
      {/* DAY 1 / DAY 2 SELECTOR BAR */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-2.5 rounded-2xl border border-slate-200 shadow-2xs">
        <div className="inline-flex p-1 bg-slate-100/90 rounded-xl gap-1">
          {/* Day 1 Button */}
          <button
            type="button"
            onClick={() => setActiveDay(EXPO_DAY_1)}
            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeDay === EXPO_DAY_1
                ? 'bg-[#0A3D27] text-white shadow-md shadow-emerald-950/20'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/80'
            }`}
          >
            <Calendar className={`w-3.5 h-3.5 ${activeDay === EXPO_DAY_1 ? 'text-[#E6C687]' : 'text-slate-400'}`} />
            <span>Day 1 — Tuesday, 27 July 2027</span>
            {day1Meetings.length > 0 && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                  activeDay === EXPO_DAY_1
                    ? 'bg-emerald-800 text-emerald-100 border border-emerald-700'
                    : 'bg-emerald-100 text-[#0A3D27]'
                }`}
              >
                {day1Meetings.length}
              </span>
            )}
          </button>

          {/* Day 2 Button */}
          <button
            type="button"
            onClick={() => setActiveDay(EXPO_DAY_2)}
            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeDay === EXPO_DAY_2
                ? 'bg-[#0A3D27] text-white shadow-md shadow-emerald-950/20'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/80'
            }`}
          >
            <Calendar className={`w-3.5 h-3.5 ${activeDay === EXPO_DAY_2 ? 'text-[#E6C687]' : 'text-slate-400'}`} />
            <span>Day 2 — Wednesday, 28 July 2027</span>
            {day2Meetings.length > 0 && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                  activeDay === EXPO_DAY_2
                    ? 'bg-emerald-800 text-emerald-100 border border-emerald-700'
                    : 'bg-emerald-100 text-[#0A3D27]'
                }`}
              >
                {day2Meetings.length}
              </span>
            )}
          </button>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 font-medium px-2">
          <Clock className="w-3.5 h-3.5 text-[#0A3D27]" />
          <span>Expo Hours: 10:00 AM – 06:00 PM</span>
        </div>
      </div>

      {/* SINGLE DAY VIEW CALENDAR CANVAS */}
      <SingleDayCalendar
        key={activeDay}
        selectedDay={activeDay}
        meetings={meetings}
        onSlotClick={onSlotClick}
        readOnly={readOnly}
        minDate={minDate}
        maxDate={maxDate}
        startHour={startHour}
        endHour={endHour}
        onDaySwitched={(day) => setActiveDay(day)}
      />
    </div>
  );
}

export default ScheduleXMeetingCalendar;
